import type { ReactNode } from 'react';
import type { BrandingSection } from '../../../wire';
import { useT } from '../../../messages';
import { SiteDialog } from '../site/SiteDialog';
import type { BlockSpec } from './specs';
import { DATA_SOURCES, offeredOn, viewsOf } from './specs';
import { Thumb } from './thumbs';
import { sectionName, sectionPurpose } from './names';

/**
 * **"Add a section"** — every kind of section this page may take, with a
 * picture, one line on what it is for, and a mark on the ones that fill
 * themselves from the business's data.
 *
 * The list is the page's (`sectionsForPage` over the merged table), minus
 * what the page cannot take twice: a block that is not repeatable and is
 * already here, and so the pinned booking band once it is on the page.
 */
export function Gallery({
  specs,
  pageKind,
  present,
  onPick,
  onClose,
}: {
  specs: Map<string, BlockSpec>;
  pageKind: string;
  present: readonly BrandingSection[];
  onPick: (type: string) => void;
  onClose: () => void;
}): ReactNode {
  const t = useT();
  const offered = galleryTypes(specs, pageKind, present);
  return (
    <SiteDialog title={t('builder.gallery.title')} wide onClose={onClose}>
      <p className="modal__consequence">{t('builder.gallery.lead')}</p>
      <ul className="bld-gallery">
        {offered.map((spec) => {
          const name = sectionName(t, spec.type, spec);
          return (
            <li key={spec.type}>
              <button
                type="button"
                className="bld-gcard"
                aria-label={t('builder.gallery.add', { name })}
                onClick={() => onPick(spec.type)}
              >
                <Thumb type={spec.type} view={viewsOf(spec)[0] ?? ''} />
                <b>{name}</b>
                <small>{sectionPurpose(t, spec.type, spec)}</small>
                {DATA_SOURCES[spec.type] === undefined ? null : (
                  <span className="bld-data-badge">{t('builder.gallery.fromData')}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="modal__actions">
        <button type="button" className="button" onClick={onClose}>
          {t('common.cancel')}
        </button>
      </div>
    </SiteDialog>
  );
}

/** The types the gallery offers on a page that already carries `present`. */
export function galleryTypes(
  specs: Map<string, BlockSpec>,
  pageKind: string,
  present: readonly BrandingSection[],
): BlockSpec[] {
  const here = new Set(present.map((s) => s.type));
  return offeredOn(specs, pageKind).filter((spec) => spec.repeatable || !here.has(spec.type));
}
